/**
 * How to play (HTP-001, owner, 2026-09-25): the rules in plain words, what the result
 * labels mean against par, and what the shot colours mean.
 *
 * HTP-002 (owner, 2026-09-25) reshaped it after Wordle's: a one-line goal, three short
 * rules, then worked examples, each a row of shots with one coloured, the way Wordle
 * shows a word with one coloured tile. Wider than Settings, and it grows with the
 * screen.
 *
 * A popup rather than a page: there are no routes until the daily system (Phase 3).
 * It borrows the Settings popup's native <dialog> and styling, so Escape, focus
 * trapping and the backdrop all come from the browser.
 */
import { DOCUMENT_LINKS, HOW_TO_PLAY, LABELS, PAR_WORDS, SCORE_LABELS } from '../config/site.ts';
import type { ShotMark } from './share.ts';
import { SHOT_MARKS, shotDot } from './shot-key.ts';

export interface HowToPlay {
  open(): void;
}

/** Shots in an example row; the rest are blank, like Wordle's uncoloured tiles. */
const EXAMPLE_LENGTH = 5;
/** Where the coloured shot sits in each row: varied, so the rows don't read as a column. */
const EXAMPLE_AT: Readonly<Record<ShotMark, number>> = { potted: 0, multi: 2, miss: 1, scratch: 3 };

/** One example row: `mark` at its place, blank everywhere else. */
export function exampleRow(mark: ShotMark): (ShotMark | undefined)[] {
  return Array.from({ length: EXAMPLE_LENGTH }, (_, i) =>
    i === EXAMPLE_AT[mark] ? mark : undefined,
  );
}

function exampleFigure(mark: ShotMark): HTMLElement {
  const { colour, text } = HOW_TO_PLAY.examples[mark];
  const figure = document.createElement('figure');
  figure.className = 'how-to-play__example';
  const row = document.createElement('div');
  row.className = 'how-to-play__row';
  row.setAttribute('aria-hidden', 'true');
  for (const slot of exampleRow(mark)) {
    if (slot !== undefined) {
      row.append(shotDot(slot));
      continue;
    }
    const blank = document.createElement('span');
    blank.className = 'shot-dot shot-dot--blank';
    row.append(blank);
  }
  const caption = document.createElement('figcaption');
  const word = document.createElement('strong');
  word.textContent = colour;
  caption.append(word, ` ${text}`);
  figure.append(row, caption);
  return figure;
}

/**
 * "2 or more under", "1 under", "Par", "1–2 over", "3 or more over": each label's band,
 * worked out from the same table the scoring uses so the two can never drift apart.
 */
export function describeBands(): { label: string; band: string }[] {
  const bands: { label: string; band: string }[] = [];
  let from = Number.NEGATIVE_INFINITY;
  for (const { atMost, label } of SCORE_LABELS) {
    bands.push({ label, band: bandText(from, atMost) });
    from = atMost + 1;
  }
  return bands;
}

function bandText(from: number, to: number): string {
  const side = (n: number): string =>
    n === 0 ? LABELS.par : `${String(Math.abs(n))} ${n < 0 ? PAR_WORDS.under : PAR_WORDS.over}`;
  if (from === Number.NEGATIVE_INFINITY)
    return `${String(Math.abs(to))} ${PAR_WORDS.orMore} ${PAR_WORDS.under}`;
  if (to === Number.POSITIVE_INFINITY)
    return `${String(from)} ${PAR_WORDS.orMore} ${PAR_WORDS.over}`;
  if (from === to) return from === 0 ? LABELS.par : side(from);
  // A band on one side of par, e.g. 1–2 over.
  return `${String(Math.abs(from))}–${String(Math.abs(to))} ${to < 0 ? PAR_WORDS.under : PAR_WORDS.over}`;
}

export function createHowToPlay(host: HTMLElement): HowToPlay {
  const c = HOW_TO_PLAY;
  const dialog = document.createElement('dialog');
  dialog.className = 'settings how-to-play';
  dialog.setAttribute('aria-labelledby', 'how-to-play-title');

  const rows = describeBands()
    .map(({ label, band }) => `<tr><th scope="row">${label}</th><td>${band}</td></tr>`)
    .join('');

  dialog.innerHTML = `
    <form method="dialog" class="settings__card how-to-play__card">
      <header class="settings__header how-to-play__header">
        <div>
          <h2 class="settings__title" id="how-to-play-title">${c.title}</h2>
          <p class="how-to-play__lede">${c.lede}</p>
        </div>
        <button type="submit" class="btn btn--icon" aria-label="${c.close}">&times;</button>
      </header>
      <ul class="how-to-play__rules">
        ${c.rules.map((rule) => `<li>${rule}</li>`).join('')}
      </ul>
      <section class="how-to-play__section" data-examples>
        <h3 class="how-to-play__heading">${c.examplesHeading}</h3>
      </section>
      <section class="how-to-play__section how-to-play__section--ruled">
        <h3 class="how-to-play__heading">${c.parHeading}</h3>
        <p>${c.par}</p>
        <table class="how-to-play__bands">
          <caption class="visually-hidden">${c.resultsHeading}</caption>
          <tbody>${rows}</tbody>
        </table>
      </section>
      <p class="how-to-play__daily">${c.daily}</p>
      <nav class="how-to-play__documents" aria-label="${c.documents}">
        ${DOCUMENT_LINKS.map((d) => `<a href="${d.href}">${d.label}</a>`).join('')}
      </nav>
    </form>
  `;
  const examples = dialog.querySelector('[data-examples]');
  if (examples === null) throw new Error('how to play: the examples section is missing');
  examples.append(...SHOT_MARKS.map(exampleFigure));
  host.append(dialog);

  // A press on the backdrop (outside the card) closes it, like the Settings popup.
  dialog.addEventListener('click', (event) => {
    if (event.target === dialog) dialog.close();
  });

  return {
    open() {
      dialog.showModal();
    },
  };
}
